import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ShieldCheck, UserCircle, IdentificationCard, Key, FilePdf, UsersFour, PencilSimple } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserPermissionsDialog from "@/components/UserPermissionsDialog";

const ROLES = ["admin", "manager", "jefatura", "supervisor", "almacenista", "mecanico", "operador", "viewer"];

const emptyForm = { name: "", email: "", password: "", role: "viewer", employee_id: null };

export default function Users() {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [permTarget, setPermTarget] = useState(null);
  const [pwdTarget, setPwdTarget] = useState(null);        // usuario cuyo pwd editamos
  const [pwdValue, setPwdValue] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportRegen, setExportRegen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [source, setSource] = useState("manual");  // "manual" | "hr"
  const [hrCandidates, setHrCandidates] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [search, setSearch] = useState("");

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.role || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const bulkFromHr = async () => {
    // Ask the admin to confirm since accounts are created with temp passwords
    // for every active HR employee that still lacks a login.
    const candidatesCount = hrCandidates.length;
    if (candidatesCount === 0) { toast.info(t("usersBulkNoCandidates")); return; }
    if (!window.confirm(t("usersBulkConfirm", { count: candidatesCount }) + "?")) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk-from-hr");
      toast.success(t("usersBulkCreated", { count: data.count || 0 }));
      // Download CSV with credentials
      if (data.created?.length) {
        const rows = [["Nombre", "Email", "Rol", "Puesto", "DNI", "Contraseña temporal"]];
        data.created.forEach(u => rows.push([u.name, u.email, u.role, u.position || "", u.dni || "", u.temp_password]));
        const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `credenciales-nomina-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  const roleLabelKeys = {
    admin: "admin",
    manager: "managerRole",
    jefatura: "jefaturaRole",
    supervisor: "supervisor",
    almacenista: "almacenistaRole",
    mecanico: "mecanicoRole",
    operador: "operadorRole",
    viewer: "viewer",
  };
  const roleLabel = (r) => t(roleLabelKeys[r] || r);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  // Reset dialog whenever we open it
  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setSource("manual");
    api.get("/users/hr-candidates")
      .then((r) => setHrCandidates(r.data))
      .catch(() => setHrCandidates([]));
  }, [open]);

  // Map HR position → suggested role for convenience
  const suggestRoleFromPosition = (position = "") => {
    const p = position.toLowerCase();
    if (/(gerente|jefe|superintend)/.test(p)) return "jefatura";
    if (/(supervisor|capataz)/.test(p)) return "supervisor";
    if (/(almacen|bodega|kardex)/.test(p)) return "almacenista";
    if (/(mec[aá]nico|mant)/.test(p)) return "mecanico";
    if (/(operador|perforista|jumbero|scoop|choferista)/.test(p)) return "operador";
    return "viewer";
  };

  const pickEmployee = (empId) => {
    const emp = hrCandidates.find((e) => e.id === empId);
    if (!emp) return;
    setForm((f) => ({
      ...f,
      employee_id: emp.id,
      name: emp.name || f.name,
      email: emp.email || f.email,
      role: suggestRoleFromPosition(emp.position),
    }));
  };

  const submit = async () => {
    try {
      const payload = { ...form };
      if (source !== "hr") payload.employee_id = null;
      await api.post("/users", payload);
      toast.success(t("newUser") + " ✓");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.put(`/users/${userId}`, { role });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (userId) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/users/${userId}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditName(u.name || "");
    setEditEmail(u.email || "");
  };

  const saveEdit = async () => {
    if (!editName.trim() || !editEmail.trim()) {
      toast.warning("Nombre y correo no pueden estar vacíos");
      return;
    }
    setEditBusy(true);
    try {
      await api.put(`/users/${editTarget.id}`, { name: editName.trim(), email: editEmail.trim() });
      toast.success(`Usuario ${editName} actualizado ✓`);
      setEditTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEditBusy(false); }
  };

  const changePassword = async () => {
    if (!pwdValue || pwdValue.length < 6) {
      toast.warning("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setPwdBusy(true);
    try {
      await api.put(`/users/${pwdTarget.id}`, { password: pwdValue });
      toast.success(`Contraseña actualizada para ${pwdTarget.email} ✓`);
      setPwdTarget(null);
      setPwdValue("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setPwdBusy(false); }
  };

  const exportCredentials = async () => {
    if (exportRegen) {
      const ok = window.confirm(
        "⚠ Se generarán contraseñas NUEVAS para TODOS los usuarios.\n" +
        "Las contraseñas actuales dejarán de funcionar inmediatamente.\n\n" +
        "¿Continuar?"
      );
      if (!ok) return;
    }
    setExportBusy(true);
    try {
      const res = await api.post("/users/credentials/pdf",
        { user_ids: [], regenerate: exportRegen },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportRegen ? "credenciales-nuevas.pdf" : "usuarios.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(exportRegen ? "Credenciales regeneradas y descargadas ✓" : "PDF descargado ✓");
      setExportOpen(false);
      setExportRegen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setExportBusy(false); }
  };

  const canSubmit = useMemo(() => {
    if (!form.name || !form.email || !form.password) return false;
    if (source === "hr" && !form.employee_id) return false;
    return true;
  }, [form, source]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline-label">{t("users")}</p>
          <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1">{t("users")}</h1>
        </div>
        <div className="flex gap-2">
        <Button
          variant="outline"
          data-testid="users-bulk-from-hr"
          onClick={bulkFromHr}
          disabled={bulkBusy}
          className="gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
        >
          <UsersFour size={16} weight="bold" /> {t("usersBulkFromHr")}
        </Button>
        <Button
          variant="outline"
          data-testid="export-credentials-button"
          onClick={() => { setExportOpen(true); setExportRegen(false); }}
          className="gap-2 border-slate-300"
        >
          <FilePdf size={16} weight="bold" /> Exportar credenciales
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-button" className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={16} weight="bold" /> {t("newUser")}</Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("newUser")}</DialogTitle></DialogHeader>

            {/* Source toggle */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg">
              <button
                type="button"
                data-testid="user-source-manual"
                onClick={() => setSource("manual")}
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition ${source === "manual" ? "bg-white text-blue-900 shadow-sm" : "text-slate-500"}`}
              >
                <UserCircle size={14} weight="bold" /> {t("userSourceManual")}
              </button>
              <button
                type="button"
                data-testid="user-source-hr"
                onClick={() => setSource("hr")}
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition ${source === "hr" ? "bg-white text-blue-900 shadow-sm" : "text-slate-500"}`}
              >
                <IdentificationCard size={14} weight="bold" /> {t("userSourceFromHR")}
              </button>
            </div>

            <div className="space-y-4">
              {source === "hr" && (
                <div className="space-y-1.5">
                  <Label className="overline-label">{t("userSourceFromHR")}</Label>
                  {hrCandidates.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">{t("noHRCandidates")}</p>
                  ) : (
                    <Select value={form.employee_id || ""} onValueChange={pickEmployee}>
                      <SelectTrigger data-testid="user-employee-select">
                        <SelectValue placeholder={t("selectEmployee")} />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-72">
                        {hrCandidates.map((e) => (
                          <SelectItem key={e.id} value={e.id} data-testid={`hr-candidate-${e.dni || e.id}`}>
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">{e.name}</span>
                              <span className="text-[10px] text-slate-500">
                                {[e.dni, e.position, e.area].filter(Boolean).join(" · ")}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              <div className="space-y-1.5"><Label className="overline-label">{t("name")}</Label>
                <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">{t("email")}</Label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">{t("password")}</Label>
                <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">{t("role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <Button data-testid="user-save-button" onClick={submit} disabled={!canSubmit} className="bg-blue-900 hover:bg-blue-800 w-full">{t("create")}</Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="p-2.5 border-b border-slate-200">
          <Input
            data-testid="users-search-input"
            placeholder="Buscar por nombre, email o rol…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-sm"
          />
          {search && (
            <p className="text-[10px] text-slate-500 mt-1">
              Mostrando {filteredUsers.length} de {users.length}
            </p>
          )}
        </div>
        <table className="w-full data-table">
          <thead><tr><th>{t("name")}</th><th>{t("email")}</th><th>{t("role")}</th><th>Ajustes</th><th>Seguridad</th><th>Editar</th><th></th></tr></thead>
          <tbody data-testid="users-table-body">
            {filteredUsers.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-6 italic">
                {search ? `Sin resultados para "${search}"` : "Sin usuarios"}
              </td></tr>
            )}
            {filteredUsers.map((u) => {
              const gr = (u.permissions_grant || []).length;
              const dn = (u.permissions_deny || []).length;
              return (
                <tr key={u.id}>
                  <td className="font-medium">
                    {u.name}
                    {u.employee_id && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5"
                            title="Vinculado a Nómina">Nómina</span>
                    )}
                  </td>
                  <td className="text-slate-500">{u.email}</td>
                  <td>
                    {u.id === user.id ? (
                      <span className="text-xs uppercase tracking-wider font-bold text-blue-900">{roleLabel(u.role)}</span>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                        <SelectTrigger className="w-44 h-8" data-testid={`user-role-change-${u.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                  <td>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5"
                            data-testid={`user-perms-${u.email}`}
                            onClick={() => setPermTarget(u)}>
                      <ShieldCheck size={13} weight="bold" /> Permisos
                      {(gr + dn) > 0 && (
                        <span className="text-[10px] font-bold text-blue-900 bg-blue-100 rounded-full px-1.5 py-0.5 ml-1">
                          +{gr}/−{dn}
                        </span>
                      )}
                    </Button>
                  </td>
                  <td>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5"
                            data-testid={`user-pwd-${u.email}`}
                            onClick={() => { setPwdTarget(u); setPwdValue(""); }}>
                      <Key size={13} weight="bold" /> Contraseña
                    </Button>
                  </td>
                  <td>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5"
                            data-testid={`user-edit-${u.email}`}
                            onClick={() => openEdit(u)}>
                      <PencilSimple size={13} weight="bold" /> Editar
                    </Button>
                  </td>
                  <td className="text-right">
                    {u.id !== user.id && (
                      <button data-testid={`user-delete-${u.email}`} onClick={() => del(u.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash size={16} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <UserPermissionsDialog
        open={!!permTarget}
        target={permTarget}
        onOpenChange={(o) => !o && setPermTarget(null)}
        onSaved={load}
      />

      {/* Editar nombre / correo */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <PencilSimple size={18} weight="bold" className="text-blue-900" /> Editar usuario
            </DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-xs">
                <p className="font-semibold text-slate-900">{editTarget.name}</p>
                <p className="text-slate-500">Rol actual: {roleLabel(editTarget.role)}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">{t("name")}</Label>
                <Input data-testid="edit-user-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">{t("email")}</Label>
                <Input data-testid="edit-user-email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              </div>
              <p className="text-[11px] italic text-slate-500">
                Para cambiar rol usa el selector; para contraseña usa el botón &quot;Contraseña&quot;.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
            <Button
              onClick={saveEdit}
              disabled={editBusy || !editName.trim() || !editEmail.trim()}
              data-testid="edit-user-save"
              className="bg-blue-900 hover:bg-blue-800"
            >
              {editBusy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cambiar contraseña */}
      <Dialog open={!!pwdTarget} onOpenChange={(o) => { if (!o) { setPwdTarget(null); setPwdValue(""); } }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Key size={18} weight="bold" className="text-blue-900" /> Cambiar contraseña
            </DialogTitle>
          </DialogHeader>
          {pwdTarget && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm">
                <p className="font-semibold text-slate-900">{pwdTarget.name}</p>
                <p className="text-slate-500 text-xs">{pwdTarget.email} · {roleLabel(pwdTarget.role)}</p>
              </div>
              <div>
                <Label className="overline-label">Nueva contraseña</Label>
                <Input
                  data-testid="pwd-change-input"
                  type="text"
                  value={pwdValue}
                  onChange={(e) => setPwdValue(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                />
                <div className="flex justify-end mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      // Genera una contraseña sugerida localmente
                      const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
                      let out = "";
                      const arr = new Uint32Array(10);
                      window.crypto.getRandomValues(arr);
                      for (let i = 0; i < 10; i += 1) out += chars[arr[i] % chars.length];
                      setPwdValue(out + "!");
                    }}
                    className="text-[11px] text-blue-900 hover:underline"
                    data-testid="pwd-generate-random"
                  >Generar aleatoria</button>
                </div>
              </div>
              <p className="text-[11px] italic text-slate-500">
                La contraseña se aplica inmediatamente. Anota o comparte por canal seguro; no se puede recuperar después.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwdTarget(null); setPwdValue(""); }}>Cancelar</Button>
            <Button
              onClick={changePassword}
              disabled={pwdBusy || pwdValue.length < 6}
              data-testid="pwd-change-save"
              className="bg-blue-900 hover:bg-blue-800"
            >
              {pwdBusy ? "Guardando…" : "Actualizar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exportar credenciales PDF */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FilePdf size={18} weight="bold" className="text-blue-900" /> Exportar lista de usuarios
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Se exportarán los <b>{users.length} usuarios</b> registrados con nombre, correo, rol y vínculo a nómina.
            </p>
            <label className="flex items-start gap-2 p-3 rounded-md border border-amber-200 bg-amber-50 cursor-pointer">
              <input
                type="checkbox"
                data-testid="export-regenerate-checkbox"
                checked={exportRegen}
                onChange={(e) => setExportRegen(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <p className="text-sm font-semibold text-amber-900">Regenerar contraseñas al exportar</p>
                <p className="text-[11px] text-amber-800 leading-snug">
                  Las contraseñas se guardan hasheadas y no pueden recuperarse. Si marcas esta opción, se generarán
                  contraseñas NUEVAS aleatorias para cada usuario, se guardarán en la base de datos y se incluirán
                  en el PDF en texto claro (única vez que serán visibles).
                </p>
              </div>
            </label>
            {exportRegen && (
              <p className="text-[11px] italic text-red-600" data-testid="export-regen-warning">
                ⚠ Las contraseñas actuales dejarán de funcionar inmediatamente. Distribuye el PDF por canal seguro.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancelar</Button>
            <Button
              onClick={exportCredentials}
              disabled={exportBusy}
              data-testid="export-credentials-confirm"
              className={exportRegen ? "bg-red-600 hover:bg-red-700" : "bg-blue-900 hover:bg-blue-800"}
            >
              {exportBusy ? "Generando…" : (exportRegen ? "Regenerar y descargar" : "Descargar PDF")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
